import ShopWidget from "@/abilities/shop/features/shop-core/components/widgets/shop.widget.vue";
import { AccessAction, AccessFeature, AccessResource } from "@/shared/access-control/access-control";
import { Ability, Route, Widget } from "@/shared/environment/ability.types";
import { useToolBarMenuShopItems } from "../actions/shop-actions";
import { useFdiAuth } from "../features/fdi/composable/use-fdi-auth";
import { requiresAuthentication } from "../features/generic/api/load-generic-shop-session";
import { useSalesforceAuth } from "../features/salesforce/composable/use-salesforce-auth";
import { useSapAuth } from "../features/sap/composable/use-sap-auth";
import { useShopStatusStore } from "../features/shop-core/store/shop-status.store";
import { ShopCore } from "../types/shop-core";
import {
    LANDING_PAGE_REDIRECT,
    SHOP_ABILITY_ALIAS,
    SHOP_DEBUG_ALIAS,
    SHOP_DEBUG_LOGIN_ALIAS,
    SHOP_LIGHT_CART_ALIAS,
} from "./constants";

export class ShopAbility implements Ability {
    alias = SHOP_ABILITY_ALIAS;
    init = async () => {
        const store = useShopStatusStore();
        const hasValidConfig = await store.initShopType();
        await store.checkConnection();

        if (!hasValidConfig) {
            // disable shop ability, since without valid config there is no way to connect to the shop
            return false;
        }

        if (window.location.href.includes(LANDING_PAGE_REDIRECT)) {
            if (store.shopType === ShopCore.Type.FDI) {
                const params = new URLSearchParams(window.location.search);
                if (params.get("code") && params.get("callingPage")) {
                    await useFdiAuth().getToken(params.get("code") as string);
                }
            } else if (store.shopType === ShopCore.Type.SWAROVSKI) {
                const params = new URLSearchParams(window.location.search);
                const isSuccess = params.get("status") === "success";
                const token = params.get("salesforceShopToken");

                if (isSuccess && token) {
                    useSalesforceAuth().setJwtToken(token);
                }
            } else if (store.shopType === ShopCore.Type.BOBST) {
                await useSapAuth().connect();
            }
        } else {
            if (store.shopType === ShopCore.Type.FDI) {
                // If the shop is online, we can proceed with the shop client initialization
                await useFdiAuth().connectToShop();
            }
            if (store.shopType === ShopCore.Type.SWAROVSKI) {
                await useSalesforceAuth().connect();
            }
            if (store.shopType === ShopCore.Type.GENERIC) {
                const session = store.genericShopSession;
                if (session && requiresAuthentication(session) && session.loginPath) {
                    // Same pattern as Salesforce/SAP: remember where the user was so
                    // the landing page can resume there once auth completes (QIT-7076).
                    store.setAppEntryPath(window.location.pathname + window.location.search);
                    store.redirectToGenericLogin(session.loginPath);
                }
            }
        }
        return true;
    };
    access = {
        accessFeature: AccessFeature.shop,
        accessResource: AccessResource.shop,
        accessAction: AccessAction.query,
    };

    getWidgets = (): Array<Widget> => [
        {
            widgetContextAlias: ["shopContext"],
            widgetComponent: ShopWidget,
        },
    ];

    getMenuEntries = useToolBarMenuShopItems;

    getRoutes = (): Array<Route> => {
        // Demo/QA pages (QIT-7076) — gated on dev_generic_shop at runtime
        // rather than build mode, so they're reachable wherever that flag is
        // (dev, review, staging, and production during rollout), not just a
        // local `yarn serve`, and absent everywhere it isn't. Not linked from
        // any menu. Delete these two routes, their page components, and
        // shop-status.store.ts's mockGenericShopSession() when QIT-7077
        // retires the flag — see FEATURE.md's "Demo/QA pages" section.
        const debugAccess = {
            accessFeature: AccessFeature.dev_generic_shop,
            accessResource: AccessResource.shop,
            accessAction: AccessAction.query,
        };

        return [
            // See shop-debug-page.vue for what it's for.
            {
                alias: SHOP_DEBUG_ALIAS,
                params: [],
                getComponent: () => import("../pages/shop-debug-page.vue"),
                isRoot: true,
                access: debugAccess,
            },
            // Reached from shop-debug-page.vue — see shop-debug-fake-login-page.vue.
            {
                alias: SHOP_DEBUG_LOGIN_ALIAS,
                params: [],
                getComponent: () => import("../pages/shop-debug-fake-login-page.vue"),
                isRoot: true,
                access: debugAccess,
            },
            {
                alias: LANDING_PAGE_REDIRECT,
                params: [],
                getComponent: () => import("../pages/shop-landing-page.vue"),
                isRoot: true,
                access: {
                    accessFeature: AccessFeature.shop,
                },
            },
            {
                alias: SHOP_LIGHT_CART_ALIAS,
                params: ["checkout?"],
                getComponent: () => import("../pages/shop-light-cart.vue"),
                isRoot: true,
                access: {
                    accessFeature: AccessFeature.shop_light,
                },
            },
        ];
    };
}
